import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Modal, 
  Typography, 
  useTheme, 
  useMediaQuery,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Verificar si la app ya está instalada
    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                         (window.navigator as any).standalone ||
                         document.referrer.includes('android-app://');

    if (isAppInstalled) return;
    const wasPromptRejected = localStorage.getItem('pwaPromptRejected') === 'true';
    const promptShownRecently = localStorage.getItem('lastPromptShown') && 
      Date.now() - parseInt(localStorage.getItem('lastPromptShown')!) < 1000 * 60 * 60 * 24 * 7; // 1 semana
    
    if (wasPromptRejected || promptShownRecently) return;
    
    // Evento antes de instalar
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Mostrar después de 30 segundos si no se ha mostrado antes
    const timer = setTimeout(() => {
      if (!isAppInstalled && isMobile) {
        setIsVisible(true);
      }
    }, 30000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      setDeferredPrompt(null);
      setIsVisible(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Puedes almacenar en localStorage que el usuario rechazó
    localStorage.setItem('pwaPromptRejected', 'true');
  };

  if (!isVisible || !isMobile) return null;

  return (
    <Modal
      open={isVisible}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        backdropFilter: 'blur(3px)'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          p: 3,
          borderRadius: '16px 16px 0 0',
          boxShadow: 24,
          position: 'relative'
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <InstallMobileIcon color="primary" sx={{ fontSize: 40 }} />
        </Box>
        
        <Typography variant="h6" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
          Instalar la aplicación
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          Para una mejor experiencia, instala nuestra aplicación en tu dispositivo. 
          Tendrás acceso rápido y funcionamiento sin conexión.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            sx={{ flex: 1 }}
          >
            Ahora no
          </Button>
          
          <Button
            variant="contained"
            onClick={handleInstallClick}
            startIcon={<InstallMobileIcon />}
            sx={{ flex: 1 }}
          >
            Instalar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PWAInstallPrompt;